/**
 * Page /t pour un client SANS l'app — logique pure (sans DOM), testée par
 * src/guest/__tests__/guestPage.test.ts. L'affichage est dans main.ts.
 *
 * La puce porte {TAG_BASE_URL}/t?n={nonce}&s={signature}. Avec l'app installée, le
 * téléphone l'ouvre directement (liens universels / App Links) ; sinon il arrive ici :
 *   1. liens vers les stores (selon la plateforme) ;
 *   2. s'il vient de régler une addition préparée par le serveur, il saisit son code à
 *      6 chiffres → Cloud Function guestCheckin → visite gardée 72 h + code de bienvenue,
 *      repris par l'app à l'inscription (referrer Google Play, sinon « Coller »).
 */

export type Platform = 'ios' | 'android' | 'other';

export interface TagParams {
    nonce: string;
    signature: string;
}

/** Même format que le serveur (functions/src/checkin/policy.ts) et l'app (apps/mobile/src/utils/tagUrl.ts). */
export function parseTagParams(search: string): TagParams | null {
    let params: URLSearchParams;
    try {
        params = new URLSearchParams(search.length > 512 ? '' : search);
    } catch {
        return null;
    }
    const nonce = params.get('n') ?? '';
    const signature = params.get('s') ?? '';
    if (!/^[A-Za-z0-9_-]{16,64}$/.test(nonce) || !/^[A-Za-z0-9_-]{43}$/.test(signature)) return null;
    return { nonce, signature };
}

/**
 * Android, iPhone / iPad (iPadOS se présente comme un Mac tactile), ou autre (ordinateur).
 * L'agent utilisateur prime : un Android peut annoncer un `platform` inattendu.
 */
export function detectPlatform(userAgent: string, platform = '', maxTouchPoints = 0): Platform {
    if (/Android/i.test(userAgent)) return 'android';
    if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios';
    if (/Macintosh/i.test(userAgent) && (platform === 'MacIntel' || platform === '') && maxTouchPoints > 1) return 'ios';
    return 'other';
}

export interface StoreLink {
    store: 'app_store' | 'google_play';
    label: string;
    /** null : lien pas encore connu → « Bientôt disponible ». */
    href: string | null;
}

const STORE_HOSTS: Record<StoreLink['store'], string> = {
    app_store: 'apps.apple.com',
    google_play: 'play.google.com',
};

function safeStoreUrl(store: StoreLink['store'], raw: string): URL | null {
    if (!raw) return null;
    try {
        const url = new URL(raw);
        return url.protocol === 'https:' && url.hostname === STORE_HOSTS[store] && !url.username && !url.password ? url : null;
    } catch {
        return null;
    }
}

/**
 * Boutons à afficher : le store de la plateforme (les deux sur ordinateur). Sur Google
 * Play, le code de bienvenue part dans le referrer d'installation (`referrer=claim=…`,
 * encodé) : l'app le lit au premier lancement (expo-application getInstallReferrerAsync).
 */
export function storeLinks(
    config: { appStoreUrl: string; playStoreUrl: string },
    platform: Platform,
    welcomeCode?: string | null,
): StoreLink[] {
    const appStore = safeStoreUrl('app_store', config.appStoreUrl);
    const play = safeStoreUrl('google_play', config.playStoreUrl);
    if (play && welcomeCode) play.searchParams.set('referrer', `claim=${welcomeCode.replace(/[^A-Z0-9]/gi, '').toUpperCase()}`);
    const links: StoreLink[] = [
        { store: 'app_store', label: 'App Store', href: appStore ? appStore.toString() : null },
        { store: 'google_play', label: 'Google Play', href: play ? play.toString() : null },
    ];
    if (platform === 'ios') return links.filter((l) => l.store === 'app_store');
    if (platform === 'android') return links.filter((l) => l.store === 'google_play');
    return links;
}

/** Code de l'addition saisi (« 482 913 », « 482913 ») → 6 chiffres, ou null. */
export function normalizeBillCode(raw: string): string | null {
    const code = raw.replace(/[\s.\-]/g, '');
    return /^\d{6}$/.test(code) ? code : null;
}

/** Clé aléatoire du navigateur (base64url, 128 bits) : rend l'envoi du code rejouable. */
export function newClientKey(getRandomValues: (a: Uint8Array) => Uint8Array): string {
    const bytes = getRandomValues(new Uint8Array(16));
    let bin = '';
    for (const b of bytes) bin += String.fromCharCode(b);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Région des Cloud Functions (par défaut, aucune région n'est fixée dans functions/). */
export const FUNCTIONS_REGION = 'us-central1';

/**
 * Adresse de la fonction : appel DIRECT (pas de réécriture Hosting), pour que la limite de
 * débit par IP voie l'adresse réelle du visiteur (functions/src/guest/guestCheckin.ts).
 */
export function guestFunctionUrl(projectId: string, emulatorHost?: string | null): string {
    if (emulatorHost) return `http://${emulatorHost}/${projectId}/${FUNCTIONS_REGION}/guestCheckin`;
    return `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net/guestCheckin`;
}

// ── Réponses de guestCheckin ─────────────────────────────────────────────────

export type GuestResult =
    | { kind: 'info'; restaurantName: string }
    | { kind: 'kept'; restaurantName: string; welcomeCode: string; expiresAt: number; amountCents: number }
    | { kind: 'refused'; reason: string; restaurantName?: string }
    /** Trop de codes faux récemment : résoudre le défi (pow.ts) puis renvoyer le code avec la preuve. */
    | { kind: 'challenge'; challenge: string; bits: number }
    | { kind: 'error'; reason: string };

/** Réponse du protocole « callable » ({ result } ou { error: { status, details } }). */
export function parseCallableResponse(httpOk: boolean, body: unknown): GuestResult {
    const b = (body && typeof body === 'object' ? body : {}) as { result?: any; error?: any };
    if (b.error && typeof b.error === 'object') {
        const status = String(b.error.status ?? '');
        const reason = typeof b.error.details?.reason === 'string' ? b.error.details.reason : status.toLowerCase() || 'unknown';
        return { kind: 'error', reason: status === 'RESOURCE_EXHAUSTED' ? 'rate_limited' : reason };
    }
    const r = b.result;
    if (!httpOk || !r || typeof r !== 'object') return { kind: 'error', reason: 'unknown' };
    const restaurantName = typeof r.restaurantName === 'string' && r.restaurantName.trim()
        ? r.restaurantName.trim().slice(0, 80) : undefined;
    if (r.success === true && typeof r.welcomeCode === 'string' && /^[2-9A-HJKMNP-Z]{4}-[2-9A-HJKMNP-Z]{4}$/.test(r.welcomeCode)) {
        return {
            kind: 'kept',
            restaurantName: restaurantName ?? 'ce restaurant',
            welcomeCode: r.welcomeCode,
            expiresAt: Number(r.expiresAt) || 0,
            amountCents: Math.max(0, Math.round(Number(r.amountCents) || 0)),
        };
    }
    if (r.success === true) return { kind: 'info', restaurantName: restaurantName ?? 'ce restaurant' };
    if (r.message === 'challenge_required') {
        const bits = Number(r.bits);
        return typeof r.challenge === 'string' && /^[0-9a-z]+\.\d+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(r.challenge)
            && r.challenge.length <= 64 && Number.isInteger(bits) && bits >= 1 && bits <= 24
            ? { kind: 'challenge', challenge: r.challenge, bits }
            : { kind: 'error', reason: 'unknown' };
    }
    return { kind: 'refused', reason: typeof r.message === 'string' ? r.message : 'unknown', ...(restaurantName ? { restaurantName } : {}) };
}

/** La puce elle-même est inutilisable : pas de saisie de code, seulement les stores. */
export function tagUnusable(reason: string): boolean {
    return ['invalid_tag', 'tag_revoked', 'tag_expired', 'restaurant_inactive'].includes(reason);
}

/** Message affiché au client (français, vouvoiement). */
export function messageFor(reason: string): string {
    switch (reason) {
        case 'bad_bill_code':
            return 'Code incorrect. Vérifiez le code à 6 chiffres affiché par le serveur.';
        case 'invalid_bill_code':
            return 'Le code de l\'addition compte 6 chiffres.';
        case 'bill_code_suspended':
            return 'La saisie du code est momentanément suspendue dans ce restaurant. Réessayez dans quelques minutes.';
        case 'target_gone':
            return 'Cette addition n\'est plus disponible (déjà enregistrée, annulée ou expirée). Demandez au serveur de la préparer à nouveau.';
        case 'invalid_tag':
            return 'Cette puce n\'est pas reconnue. Vous pouvez tout de même installer l\'app.';
        case 'tag_revoked':
            return 'Cette puce a été désactivée par le restaurant.';
        case 'tag_expired':
            return 'Cette puce a expiré : signalez-le au restaurant.';
        case 'restaurant_inactive':
            return 'Ce restaurant ne valide pas de visites pour le moment.';
        case 'rate_limited':
            return 'Trop de tentatives. Patientez quelques minutes avant de réessayer.';
        case 'network':
            return 'Connexion impossible. Vérifiez votre réseau et réessayez : votre code reste valable quelques minutes.';
        case 'challenge_failed':
            return 'La vérification de sécurité n\'a pas abouti. Réessayez dans un instant.';
        default:
            return 'Une erreur est survenue. Réessayez dans un instant.';
    }
}

// ── Mise en forme ────────────────────────────────────────────────────────────

export function formatEuros(cents: number): string {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

/** « jeudi 30 octobre à 21:30 » (fuseau du téléphone). */
export function formatExpiry(ms: number, timeZone?: string): string {
    const date = new Date(ms);
    const day = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', ...(timeZone ? { timeZone } : {}) }).format(date);
    const time = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', ...(timeZone ? { timeZone } : {}) }).format(date);
    return `${day} à ${time}`;
}

/** « ABCD-EFGH » épelé pour les lecteurs d'écran : « A B C D, tiret, E F G H ». */
export function spellCode(code: string): string {
    return code.split('-').map((part) => part.split('').join(' ')).join(', tiret, ');
}

// ── Visite gardée, mémorisée dans le navigateur (rechargement, retour sur la page) ──

export interface StoredClaim {
    welcomeCode: string;
    expiresAt: number;
    restaurantName: string;
    amountCents: number;
}

/**
 * Clé du navigateur (rejeu d'une réponse perdue) : gardée seulement tant qu'une visite est
 * active ou qu'un envoi date de moins de 15 min (fenêtre de rejeu du serveur) ; sinon effacée.
 */
export const CLIENT_KEY_GRACE_MS = 15 * 60_000;

export function keepClientKey(activeClaims: number, lastSubmitAt: number | null, nowMs: number): boolean {
    return activeClaims > 0 || (lastSubmitAt !== null && nowMs - lastSubmitAt < CLIENT_KEY_GRACE_MS);
}

/** Lien « Confidentialité » du pied de page : https seulement, sinon pas de lien (pas de lien cassé). */
export function safePrivacyUrl(raw: string): string | null {
    if (!raw) return null;
    try {
        const url = new URL(raw);
        return url.protocol === 'https:' && !url.username && !url.password ? url.toString() : null;
    } catch {
        return null;
    }
}

/** Codes gardés au plus dans le navigateur (plusieurs visites sans l'app dans les 72 h). */
export const MAX_STORED_CLAIMS = 5;

function claimFrom(c: any, nowMs: number): StoredClaim | null {
    if (c && typeof c.welcomeCode === 'string' && /^[2-9A-HJKMNP-Z]{4}-[2-9A-HJKMNP-Z]{4}$/.test(c.welcomeCode)
        && Number(c.expiresAt) > nowMs) {
        return {
            welcomeCode: c.welcomeCode,
            expiresAt: Number(c.expiresAt),
            restaurantName: typeof c.restaurantName === 'string' ? c.restaurantName.slice(0, 80) : 'ce restaurant',
            amountCents: Math.max(0, Math.round(Number(c.amountCents) || 0)),
        };
    }
    return null;
}

/** Visites gardées encore valides, la plus récente d'abord (liste, ou ancien format à un seul code). */
export function parseStoredClaims(raw: string | null, nowMs: number): StoredClaim[] {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        const list = Array.isArray(parsed) ? parsed : [parsed];
        return list.map((c) => claimFrom(c, nowMs)).filter((c): c is StoredClaim => !!c).slice(0, MAX_STORED_CLAIMS);
    } catch {
        return []; // valeur corrompue : ignorée
    }
}

/** Ajoute une visite gardée en tête, sans doublon ni code expiré : une 2e visite n'efface pas la 1re. */
export function addStoredClaim(list: StoredClaim[], claim: StoredClaim, nowMs: number): StoredClaim[] {
    return [claim, ...list.filter((c) => c.welcomeCode !== claim.welcomeCode && c.expiresAt > nowMs)].slice(0, MAX_STORED_CLAIMS);
}
